import type { BioBanks, BioTopic, OneLinerRegister, Weighted, ZodiacSign } from '../types.js';

/**
 * Turkish bio material. Written the way 19–26 year-olds on a Turkish social
 * app actually write: short, often a fragment, half the time lowercase, no
 * full stop. Bank strings are emoji-free (the engine adds emoji) and name no
 * platform, handle or number.
 */

const ONE_LINERS_FUNNY: string[] = [
  // ported
  'Profesyonel uyuyucu, amatör insan.',
  'Pizzayla ilişkim çoğu insandan uzun sürdü.',
  'Hayatta iki şey severim: {hobby} ve burada olmamak.',
  'Planım yok ama özgüvenim tam.',
  'Sabahları insan değilim, akşamları emin değilim.',
  'Komik olduğumu düşünüyorum, kanıt yok.',
  'Telefon şarjım gibiyim: hep %20.',
  'Diyet yarın başlıyor (her gün yarın).',
  'Yürüyüşe çıkarım, buzdolabına kadar.',
  'Karar veremem, menüyü bile zor seçerim.',
  'Spor salonu üyeliğim var, salonun haberi yok.',
  '{city} trafiğinde büyüdüm, sabrım o yüzden yok.',
  'Hayatım bir dizi olsa kimse 2. sezonu izlemezdi.',
  'Yetişkin gibi davranıyorum, kimse fark etmiyor.',
  'En büyük yeteneğim: {hobby} derken zamanı unutmak.',
  'Sabah alarmı 6, kalkış saati: bilinmiyor.',
  'Sosyal hayatım takvimde var, gerçekte yok.',
  'Motivasyonum yüksek, enerjim düşük, ikisi hiç buluşmuyor.',
  '"Az yiyeceğim" diyerek yola çıktım, ne olduysa oldu.',
  'Kitap okuyacağım, dizi açtım, nasıl oldu bilmiyorum.',
  '{city}de yaşıyorum, romantik hayatım da onun kadar karmaşık.',
  'Bütçem ile hayallerim arasında ciddi bir iletişim sorunu var.',
  'Erken yatacağım diye yemin ettim, saat 02.00.',
  'Planlarım mükemmel, uygulamam trajik.',
  'Hobim {hobby}, asıl işim bahane üretmek.',
  'Fatura ödedim, ayın geri kalanında yaratıcıyım.',
  'Derin sohbetleri severim, başlatmayı bilmiyorum.',
  'Her şeye başlarım, hiçbirini bitirmem',
  'Sabah koşusu yaparım, yani hayal ederim.',
  // new
  'annem hala yemek yedin mi diye arıyor',
  'çay demlemeyi bilirim başka yeteneğim yok',
  'boyum kısa sabrım daha kısa',
  'kahvaltı insanıyım, akşam yemeği insanı da',
  'mesajı görürüm cevap vermeyi unuturum kusura bakma şimdiden',
  'sınav haftası beni görmeyin',
  'kedim beni sevmiyor ama yine de çabalıyorum',
  'gece 3te en mantıklı kararlarımı veririm',
  'bir gün erken kalkacağım, o gün bugün değil',
  'yüzüm asık ama içim güler, söz',
  'ilk mesajı atmam, ikinciyi hiç atmam',
  'pazar sabahı uyandırma, sonuçlarına katlanamazsın',
  'yemek yapabiliyorum ama sadece makarna',
  'dizi önerisi alırım, gerçekten izlerim mi orası belirsiz',
  'çekingenim ama bir açılırsam susmam',
  '{team} maçı varsa telefonu kapatırım, yenilirse haftaya kadar',
  'annemin tatlı kızı, babamın masraf kalemi',
  'kebap sevmeyen biriyle anlaşamam diye bir kural koydum',
  'normalde böyle uygulamalara girmem, sıkıldım',
  'kanka beni buraya zorla kaydetti',
  'üniversite bitmiyor ben bitiyorum',
  'cumartesi gecesi evde dizi izleyen asi bir ruh',
  'karnım acıkınca huysuzlaşırım, önlem al',
  '{school} okuyorum, hayatı sorgulamaya devam',
  'iki dakikada hazırım dediğimde yarım saat demek',
  'kahve içmeden konuşma, içtikten sonra da az konuş',
  'ehliyetim var cesaretim yok',
  'kombi gibiyim, ısınmam zaman alıyor',
  'sabah dersi seçen benim, pişman olan da benim',
  '{city}de yağmur yağınca şemsiye almayan kişi',
  'listemde gidilecek 40 yer var, gittiğim 2',
  'el falıma bakan teyze büyük şeyler söyledi, bekliyorum',
  'mantı sever, sarımsaklı yoğurt tartışmaya kapalı',
  'yürüyüşe çıkıp börekçide bitirebilirim',
  'kısa boylu, uzun hikayeli',
  'gece kuşuyum, sabahları kuş bile değilim',
  'en iyi pozum uyurken çekilen',
  'ikinci el kitap ve birinci el uykusuzluk',
  'evin en küçüğü olduğum için her şeyi hak ediyorum',
  'çok konuşurum ama sonra özür dilerim',
  'kapıyı çekip çıkarım, anahtar içeride kalır, klasik',
  '{trait} ama sadece pazartesileri',
  'kahveyi şekersiz içerim, hayatı değil',
  'saat kaç bilmem ama çay saati olduğunu bilirim',
  'bir gün düzenli biri olacağım, o gün bugün değil',
  'en sevdiğim spor uzanmak',
  'ilk buluşmada yemek seçimi benden, ödeme senden şaka şaka',
  'menemen soğanlı olur, gelin tartışalım',
  'cebimde hep bir bahane var',
  'geceleri buzdolabıyla derin sohbetler',
  'astrolojiye inanmam ama {zodiac} olduğum her halimden belli',
  'iş görüşmesi gibi oldu bu bio, iyi mi',
  'sınavdan önceki gece öğrenen nesil',
  'bir bardak çay, bir avuç çekirdek, mutluluk',
  'tek eksiğim bir köpek ve bir bahçeli ev, bir de para',
  'yeme içme konusunda çok seçici değilim, her şeyi yerim',
  'otobüste uyuyup durağı kaçıran biri',
  'ayakkabı alırım, giymem, saklarım',
  'çamaşır makinesi bitti diye sevinen yetişkin',
  'saçımı kestirdim pişman oldum, klasik ben',
  'iki kelimeyle anlatayım: aç ve uykulu',
  'bütün şarkıları yanlış sözlerle söylerim',
  'yolda birini selamlarım o başkasına el sallıyormuş',
  'alışveriş sepetim dolu, hesabım boş',
  'erkenci değilim ama geç kalmam, genelde',
  'hayalim emekli olmak, henüz işe başlamadım',
  'çikolata bitti diye üzülen 24 yaşında bir bebek',
  'yarım saat soğan doğradım ağlamadım, güçlü biriyim',
  'market alışverişine aç gidip pişman olan',
  'oda temizledim, kayıp eşyalarımın yarısı çıktı',
  'bulaşık makinesini boşaltmak benim kardiyom',
  'not ortalamam düşük, moralim yüksek',
  'kahvemi soğutmadan içebildiğim gün yok',
];

const ONE_LINERS_CATCHY: string[] = [
  // ported
  'Az konuşur çok yaparım.',
  'Drama yok, vibe var.',
  'Gerisini sohbette anlatırım.',
  'Sade ama akılda kalıcı.',
  'Ne ararsan o değilim, daha iyisiyim.',
  'Enerjimi boşa harcamam.',
  'Plan yapmam, yaşarım.',
  'Sözüm az, duruşum belli.',
  'Kalite zaman, boş muhabbet yok.',
  'Kendi yolumda, acelem yok.',
  'Gülerim ama herkese değil.',
  'İyi müzik, iyi insan, gerisi teferruat.',
  'Mesafemi bilirim, değerimi de.',
  'Az ama gerçek.',
  'Sıradan değil, sade.',
  'Filtrelerim kendi içimde.',
  'Seçici olmak erdem.',
  'Standartlarımdan taviz vermem.',
  'Konuşmadan önce düşünürüm, bu fark yaratır.',
  'Merak ediyorsan sor.',
  'Vakit varsa değer var.',
  'Gerçeklik abartıdan iyidir.',
  'Tarzım tartışılmaz.',
  'İlk izlenim bende yanılmıyor.',
  'Kalabalıktan değil, seçilmişlerden yanayım.',
  'Kanıtlamam gereken bir şey yok.',
  'Hızlı tüketmem, uzun tadarım.',
  'Rahat ol, ben de rahatım.',
  'Olduğun gibi gel.',
  'Fazlası zarar.',
  // new
  'boş yapma',
  'sohbet iyiyse gerisi gelir',
  'akışında',
  'merhaba demek bedava',
  'kısa yazdım, uzun konuşuruz',
  'kendimle barışığım',
  'ne olduğumu biliyorum',
  'saygı karşılıklıdır',
  'yazmadan önce bir düşün',
  'bio yazmayı sevmem, sor',
  'hayat kısa, kahve sıcak',
  '{city} | {hobby}',
  '{city} ve {hobby}',
  'sadece iyi vibe',
  'gerçek insanlar gelsin',
  'bilen bilir',
  'kendi kafamda takılıyorum',
  'sakin kafa',
  'ne o öyle bakıyorsun',
  'tanışalım, sonra karar ver',
  'ilk mesaj senden',
  'samimiyet şart',
  'drama sevmem',
  'basit ama sağlam',
  'boş muhabbete kapalıyım',
  'merhaba yaz yeter',
  'gülümse, bedava',
  'yolum belli',
  'dertsiz tasasız',
  'ciddi olan yazsın',
  'tanıyınca seversin',
  'az ve öz',
  'keyfim yerinde, sen nasılsın',
  'kafa dengi arıyorum',
  '{trait} ve {hobby}',
  'her şey zamanla',
  'iyi insan iyi muhabbet',
  'geri adım yok',
  'yaşamak lazım',
  'olduğum gibiyim',
  'güzel şeyler olacak',
  '{city} doğumlu, {hobby} meraklısı',
  'yorgunum ama mutluyum',
  'sevdiğim şeyler: {hobby}, {hobby} ve uyku',
  'değerini bilene',
  'herkes kendi hikayesinin kahramanı',
  'şimdilik bu kadar',
  'gerisi sende',
  'bakmakla olmuyor, yaz',
  'açık sözlüyüm, alınma',
  'ne istediğimi biliyorum',
  'gülmeyi seven kazanır',
  'sade yaşa',
  'bugünün tadını çıkar',
  'yarın düşünürüz',
  'kafana göre takıl',
  'düz insanım',
  'sabah kahve akşam çay',
  'çok şey bekleme, sürpriz yapayım',
  'enerjine bakarım',
  'sözünün eri',
  'kalbi temiz olanlar',
  'umursamaz değilim, seçiciyim',
  'özel hayat özeldir',
  'buradayım işte',
  'kısa ve öz: {trait}',
  'yazarsan cevap veririm',
  'kahve ısmarlayan kazanır',
  'gelen gideni aratmasın',
  'eğlenmeyi bilen',
  'iyi ki varım',
  'yeni insanlar, yeni hikayeler',
  'hayat bu, akıyor',
  'kalp kırmam, kırılırım da',
  'bir çay içimlik muhabbet',
  'herkes kendi yolunda',
  'sorun yok, çözüm var',
  'bugün de güzel',
  'şimdiki zaman',
];

const ONE_LINERS_CRINGE: string[] = [
  // ported
  'Hayata pozitif bakan bir ruh, kahve bağımlısı, hayvansever',
  'Melek kalpli ama yanlış yaparsan bambaşkayım',
  'Yıldızların altında dans etmeyi seven bir hayalperest',
  'Kalbim {city} kadar büyük, gülüşüm güneş kadar parlak',
  'Pozitif enerji yayıyorum, negatif insanlar uzak dursun',
  'Hayat bir maceradır ve ben baş kahramanıyım',
  'Küçük şeylerden mutlu olan koca yürekli biri',
  'Ruhum özgür, kalbim sıcak, enerjim yüksek',
  'Her sabah gülümseyerek uyanan bir güneş çocuğu',
  'Sevgi dolu, ışık saçan, kelebekler gibi özgür',
  'Yağmurda dans eder, gün doğumunu kaçırmam',
  'Kahve, {hobby} ve sonsuz pozitif enerji',
  'Evrene güveniyorum, evren de bana',
  'Gülümsemem bulaşıcıdır, dikkatli ol',
  'İçimdeki çocuğu hiç büyütmedim',
  'Güzelliği her yerde görebilen büyülü bir ruhum',
  'Kalp kırmak için değil, iyileştirmek için buradayım',
  'Hayaller büyüktür, ben onları yaşıyorum',
  'Kader bizi buluşturacak, inanıyorum',
  'Dünyayı {hobby} ve sevgiyle daha güzel yapıyorum',
  'Doğanın kızıyım, bulutların üzerinde uçan biri',
  'Kalbimde sonsuz bir sevgi denizi var, boğulma dikkat et',
  'Ben sadece bir ruh değil, bir deneyimim',
  'Kaosun içinde çiçek gibi açıyorum',
  '{city} benim kalbimde, kalbim tüm dünyada',
  'Her anı tam yaşıyorum, geçmişe bakmam',
  'Çevremdeki herkese bir ışık olmaya çalışıyorum',
  'Negatif enerjiye kapalıyım, kapılar mühürlü',
  'Sevgi dilimdir, kalple konuşurum',
  'Hayatım bir şiir, her anı bir mısra',
  // new
  'beni anlayan biri var mı bu dünyada',
  'ben bir yağmur damlası, sen benim denizim olur musun',
  'kimse beni tanımıyor aslında',
  'sessizliğim çok şey anlatır, duyan olmadı',
  'gülüşümün arkasında kocaman bir hikaye var',
  'kalbim kırık ama hala güzel atıyor',
  'yanlış insanlara doğru duygular verdim',
  'kral düşerse krallık biter',
  'ben değişmedim, sadece gerçek yüzünüzü gördüm',
  'aslan yattığı yerden belli olur',
  'herkes beni anlamaz, anlayan da yanımda kalır',
  'kaybettiklerim bana kazandırdı',
  'yalnız kurt sürüye katılmaz',
  'gözlerim çok şey söyler, okumayı bilene',
  'kalbimin anahtarı bende değil, kayboldu',
  'bir kere güvenirim, ikinci şans yok',
  'sana yazılmış bir şiirim ben',
  'yüzümde gülümseme, içimde fırtına',
  'beni hak etmeyenlere teşekkürler, öğrettiniz',
  'prensesler de bazen yorulur',
  'ben bir kelebeğim, kısa ama güzel yaşarım',
  'geceyi seven, yıldızlarla konuşan biri',
  'ruhumun ikizini arıyorum',
  'sevgiye inanan son romantik',
  'denizin sesi, kahvenin kokusu, bir de sen',
  'mutluluk paylaştıkça çoğalır',
  'güneş gibi doğ, kimse senden ışığını alamaz',
  'kalbimi kimseye vermedim, ödünç bile',
  'ben farklıyım, kabul et',
  'sadık ama naif değil',
  'kimseye muhtaç değilim, sadece özlüyorum',
  'aynı gökyüzüne bakıyoruz belki de',
  'her şeyin bir sebebi var, bizim de olacak',
  'hayallerimin peşinden koşan cesur bir yürek',
  'yalnızlık bana yakışıyor ama sıkıldım',
  'kırılan kalpler daha güzel sever',
  'benimle gülen bir gün benim için ağlar mı',
  'kalbi güzel insanlara selam olsun',
  'sevgiyi hak eden herkese açığım',
  'ben bir hikayeyim, okumak cesaret ister',
  'kimse benim kadar sevemez',
  'yıllar geçer ama gerçek dostluk kalır',
  'ruhumda bir çocuk, gözlerimde bir yetişkin',
  'kader beni nereye götürürse',
  'yalnız ama tek değil',
  'ben bir denizim, dalgalarıma dikkat',
  'üzüldüysem bile güldüm, güçlüyüm',
  'aşk bir kere gelir derler, bekliyorum',
  'kırık kalpler kulübü kurucu üyesi',
  'kendi masalımın prensesiyim',
  'ben karakterimle varım, gerisi teferruat',
  'beni anlamak için kalbinle bakman lazım',
  'hayatımın filmi çekilse başrol yine benim',
  'gözlerimin rengi değil, kalbimin rengi önemli',
  'yüreği güzel olan yanıma gelsin',
  'ruhum yaşlı, kalbim genç',
  'hayat bana limon verdi, limonata yaptım',
  'karanlıktan korkmam, ben de bir yıldızım',
  'sen beni bulamazsan ben seni bulurum',
  'sevgiyle başlayan her şey güzeldir',
  'özgür ruhlu bir kalp, kanatlarımı kimse kesemez',
  'derinliğim herkese göre değil',
  'deniz kadar sakin, fırtına kadar deli',
  'ben aslında çok iyi biriyim ama',
  'iyi ki doğmuşum diyecek birini bekliyorum',
  'hayat bir yolculuksa ben en güzel manzarayım',
  'hayatın bana verdiği en güzel hediye benim',
  'gözyaşlarım bile parlıyor',
  'kalbimin sesini dinliyorum, sen de dinle',
  'ben bir rüyayım, uyanmak istemeyeceksin',
  'her gün yeniden doğan bir anka kuşu',
];

const ONE_LINERS_SINCERE: string[] = [
  // ported
  'Sessiz biriyim ama tanışınca konuşkanım.',
  'İyi sohbet ve iyi müzik yeter bana.',
  'Samimiyet benim için önemli.',
  'Yeni yerler keşfetmeyi ve sakin akşamları severim.',
  'Hayatı olduğu gibi kabul etmeye çalışıyorum.',
  'Küçük şeylerin değerini bilen biriyim.',
  'Dürüstlük benim için her şeyden önce gelir.',
  'Biraz utangacım ama tanıyınca değişir fikrin.',
  'Huzuru basit anlarda buluyorum.',
  'İyi bir dinleyiciyim, anlatmayı da severim.',
  'Doğada vakit geçirmek bana iyi geliyor.',
  'Gülmeyi ve güldürmeyi severim.',
  'Kendi halinde, sakin bir hayatım var.',
  'Gerçek bağ kurabileceğim insanlar arıyorum.',
  'Bazen dağınığım ama kalbim temiz.',
  'Kendinden bahset, dinlemeyi severim.',
  'Mükemmel olmaya çalışmıyorum, gerçek olmaya çalışıyorum.',
  'Dostluğa değer veririm.',
  'Hem ciddi hem eğlenceli olabileceğime inanıyorum.',
  'Yargılamaktan değil, anlamaktan yanayım.',
  'Kendimi geliştirmeye çalışıyorum, yavaş ama kararlı.',
  'İnsanlara güvenmem zaman alır.',
  'Bazen çok düşünürüm, en büyük kusurum bu.',
  'Rutinlerim var ama katı değilim.',
  'Beklentilerimi gerçekçi tutmaya çalışırım.',
  'Hayatta az şey istiyorum ama o az şeyi gerçekten istiyorum.',
  // new
  'yeni insanlarla tanışmak için buradayım',
  'sohbet etmeyi seven biriyim, yazabilirsin',
  'şehre yeni taşındım, arkadaş arıyorum',
  'sesli odalarda takılmayı seviyorum',
  'akşamları biraz muhabbet, biraz müzik',
  'işten sonra kafa dağıtmak için buradayım',
  'çok sosyal biri değilim ama denemek istedim',
  'arkadaş canlısıyım, kibar olan herkes yazabilir',
  'öğrenciyim, boş vakitlerimde buradayım',
  'iyi niyetli insanlarla tanışmak isterim',
  'saygılı olan herkesle konuşurum',
  'ciddi bir şey aramıyorum, sadece sohbet',
  'kafa dengi biriyle uzun uzun konuşmak isterim',
  'uykusuz geceler için muhabbet arkadaşı lazım',
  'sabah erken kalkarım, akşam erken yatarım, sıkıcıyım biraz',
  'ailemle çok yakınım',
  'hayvanları çok severim',
  'kitap okumayı ve yürüyüş yapmayı severim',
  'yemek yapmayı seviyorum, en iyisi mercimek çorbam',
  'müzik zevkim karışık, her şeyi dinlerim',
  'bazen sessiz kalırım, kötü niyetten değil',
  'iyi bir arkadaş olmaya çalışırım',
  'yalan sevmem',
  'açık sözlü biriyim, kırıcı değil',
  'tatlı dilli insanları severim',
  'hayatımda sakinlik arıyorum',
  'yeni bir şehirde yeni bir başlangıç',
  'insanları dinlemeyi seviyorum',
  'kendimi anlatmayı pek beceremem, sohbette daha iyiyim',
  'gece çalışıyorum, gündüz uyuyorum, saatlerim ters',
  'vakit geçirmek ve güzel sohbet için buradayım',
  'mezun oldum, iş arıyorum, bu arada sohbet',
  'kalabalık ortamları pek sevmem',
  'sabırlı biriyim, çoğu zaman',
  'yazın deniz, kışın sıcak çay',
  'kafamdakileri yazmakta zorlanıyorum, sorarsan anlatırım',
  'kimseyi yargılamam, beni de yargılamayın',
  'güler yüzlü insanlara karşı zaafım var',
  'bazen sadece dinlenmek isterim, bazen de saatlerce konuşmak',
  'ev hayatını seviyorum',
  'çok konuşmam ama dinlerim',
  'dürüst olan herkesle iyi anlaşırım',
  'yeni hobiler denemeyi seviyorum',
  'arkadaşlarım benim ailem gibi',
  'birlikte gülebileceğim insanlar arıyorum',
  'muhabbeti güzel olan herkese açığım',
  'gerçekten tanışmak isteyen yazsın',
  'ilk başta soğuk görünürüm, değilim',
  'hayatımda en çok huzuru önemsiyorum',
  'hafta sonları doğaya kaçarım',
  'çay ve sohbet benim için yeter',
  'insan ilişkilerinde emek önemli',
  'samimi insanları severim',
  'sözüne güvenilen biri olmaya çalışırım',
  'mütevazı biriyim, gösterişi sevmem',
  'zor zamanlardan geçtim, artık daha iyiyim',
  'kendime vakit ayırmaya çalışıyorum bu ara',
  'yalnız yaşıyorum, bazen sessizlik ağır geliyor',
  'gündüz çalışıyorum, akşamları buradayım',
  'pek fotoğraf çekinmem, sorun değil umarım',
  'beni güldüren birini arıyorum',
  'iyi kalpli insanlara denk gelmek dileğiyle',
  'hayattan beklentim basit, huzur ve sağlık',
  'geceleri uyuyamayınca buraya giriyorum',
  'yeni başladım, nasıl çalışıyor öğreniyorum',
  'akşamları balkonda çay içmeyi seviyorum',
  'fazla beklentim yok, güzel bir sohbet yeter',
  'memleketimi özlüyorum bazen',
  'iyi bir arkadaşlık her şeyden değerli',
  'sakin ve güler yüzlü biriyim galiba',
];

const HOBBIES: string[] = [
  // ported
  'müzik', 'dizi', 'film', 'kahve', 'kitap', 'spor', 'oyun', 'yürüyüş', 'kamp', 'yemek',
  'fotoğraf', 'dans', 'yoga', 'koşu', 'bisiklet', 'gitar', 'piyano', 'resim', 'seyahat', 'doğa',
  'kediler', 'köpekler', 'anime', 'podcast', 'tiyatro', 'konser', 'balık tutma', 'satranç', 'plak', 'kahvaltı',
  'deniz', 'dağ', 'lo-fi', 'rap', 'rock', 'caz', 'puzzle', 'bahçe', 'pilates', 'sörf',
  'sinema', 'roman', 'çizim', 'seramik', 'el işi', 'boks', 'yüzme', 'tenis', 'basketbol', 'voleybol',
  'mangal', 'kurabiye yapma', 'türk sanat müziği', 'heykel', 'origami', 'kutu oyunları', 'kaçış odası', 'dövüş sanatları',
  // new
  'futbol', 'halı saha', 'fitness', 'gym', 'crossfit', 'kick boks', 'muay thai', 'okçuluk', 'binicilik', 'dalış',
  'tırmanış', 'kayak', 'snowboard', 'kaykay', 'paten', 'motor', 'arabalar', 'off-road', 'drift', 'karting',
  'masa tenisi', 'bilardo', 'bowling', 'okey', 'tavla', 'batak', 'pişti', 'dart', 'e-spor', 'konsol oyunları',
  'bilgisayar oyunları', 'mobil oyunlar', 'valorant', 'lol', 'minecraft', 'fifa', 'pes', 'counter strike', 'manga', 'kore dizileri',
  'türk dizileri', 'belgesel', 'true crime', 'korku filmleri', 'bilim kurgu', 'fantastik kitaplar', 'şiir', 'yazı yazmak', 'günlük tutmak', 'blog',
  'stand-up', 'doğaçlama tiyatro', 'opera', 'bale', 'modern dans', 'salsa', 'tango', 'halay', 'zeybek', 'horon',
  'bağlama', 'ud', 'keman', 'davul', 'bateri', 'ney', 'şan', 'karaoke', 'şarkı söylemek', 'beste yapmak',
  'türkçe pop', 'türkçe rap', 'arabesk', 'türk halk müziği', 'indie', 'metal', 'elektronik müzik', 'klasik müzik', 'r&b', 'kpop',
  'kahve demleme', 'üçüncü dalga kahve', 'çay', 'pasta yapmak', 'ekmek yapmak', 'sokak lezzetleri', 'yeni mekanlar keşfetmek', 'brunch', 'vegan mutfak', 'dünya mutfağı',
  'makyaj', 'cilt bakımı', 'tırnak sanatı', 'moda', 'ikinci el alışveriş', 'vintage', 'dikiş', 'örgü', 'makrome', 'takı tasarımı',
  'mum yapımı', 'ebru', 'hat sanatı', 'kaligrafi', 'yağlı boya', 'suluboya', 'dijital çizim', 'grafik tasarım', 'video kurgu', 'fotoğraf düzenleme',
  'kodlama', 'yazılım', 'yapay zeka', 'teknoloji', 'bilim', 'astronomi', 'uzay', 'tarih', 'felsefe', 'psikoloji',
  'astroloji', 'tarot', 'kahve falı', 'meditasyon', 'nefes egzersizleri', 'kişisel gelişim', 'dil öğrenmek', 'ingilizce', 'korece', 'almanca',
  'gezmek', 'yol tripleri', 'karavan', 'otostop', 'trekking', 'doğa yürüyüşü', 'kamp ateşi', 'yıldız izlemek', 'gün batımı', 'sahil yürüyüşü',
  'balkon sohbetleri', 'gece yürüyüşleri', 'uzun yol', 'tren yolculuğu', 'adalar', 'ege kasabaları', 'karadeniz yaylaları', 'kapadokya', 'müze gezmek', 'sergiler',
  'festival', 'açık hava konserleri', 'tiyatro oyunları', 'sinema geceleri', 'film arşivi', 'eski türk filmleri', 'yeşilçam', 'çizgi film', 'animasyon', 'çizgi roman',
  'bitki yetiştirmek', 'saksı çiçekleri', 'sukulentler', 'bahçecilik', 'akvaryum', 'kuş beslemek', 'sokak hayvanları', 'gönüllülük', 'hayvan barınakları', 'koleksiyon',
  'pul koleksiyonu', 'plak koleksiyonu', 'lego', 'maket', 'rubik küpü', 'bulmaca', 'sudoku', 'zeka oyunları', 'quiz geceleri', 'kafede ders çalışmak',
];

const TRAITS: string[] = [
  // ported
  'sessiz', 'konuşkan', 'gece kuşu', 'evcimen', 'gezgin ruh', 'meraklı', 'sakin', 'enerjik', 'utangaç', 'çılgın',
  'romantik', 'gerçekçi', 'hayalperest', 'inatçı', 'rahat', 'titiz', 'esprili', 'duygusal', 'maceracı', 'tembel',
  'çalışkan', 'pozitif', 'kararsız', 'meraklı bir kafa', 'az ama öz',
  'sabırlı', 'güvenilir', 'sezgisel', 'içine kapanık', 'açık kalpli',
  'alaycı', 'nazik', 'bağımsız', 'sadık', 'esnek',
  // new
  'sabah insanı', 'gece insanı', 'kahve bağımlısı', 'çay tiryakisi', 'uykucu', 'dağınık', 'düzenli', 'planlı', 'plansız', 'spontane',
  'sosyal', 'asosyal', 'introvert', 'ekstrovert', 'hassas', 'soğukkanlı', 'sinirli ama tatlı', 'kıskanç', 'alıngan', 'mızmız',
  'neşeli', 'gülen yüzlü', 'ciddi', 'ağır abi', 'deli dolu', 'eğlenceli', 'şakacı', 'ironik', 'kara mizahçı', 'saf',
  'naif', 'mantıklı', 'analitik', 'yaratıcı', 'sanatçı ruhlu', 'meraklı kedi', 'unutkan', 'dikkatli', 'sorumluluk sahibi', 'hırslı',
  'tutkulu', 'kararlı', 'azimli', 'umursamaz', 'cömert', 'tutumlu', 'savurgan', 'misafirperver', 'ev kuşu', 'sokak çocuğu',
  'kafa dengi', 'açık sözlü', 'diplomatik', 'kibar', 'dobra', 'geveze', 'az konuşan', 'iyi dinleyici', 'yardımsever', 'koruyucu',
  'merhametli', 'empatik', 'idealist', 'mükemmeliyetçi', 'detaycı', 'aceleci', 'sabırsız', 'telaşlı', 'huzur arayan', 'nostaljik',
  'romantik ama gizli', 'kuralcı', 'asi', 'özgür ruhlu', 'aile canlısı', 'arkadaş canlısı', 'hayvansever', 'doğasever', 'oyuncu', 'kitap kurdu',
  'film manyağı', 'dizi bağımlısı', 'müzik kulaklı', 'gurme', 'obur', 'sağlıklı yaşamcı', 'sporcu', 'hareketli', 'tembel ama tatlı', 'çok yönlü',
];

const CITIES: Weighted[] = [
  { name: 'İstanbul', weight: 160 }, { name: 'Ankara', weight: 57 }, { name: 'İzmir', weight: 44 },
  { name: 'Bursa', weight: 32 }, { name: 'Antalya', weight: 27 }, { name: 'Konya', weight: 23 },
  { name: 'Adana', weight: 22 }, { name: 'Gaziantep', weight: 21 }, { name: 'Kocaeli', weight: 21 },
  { name: 'Mersin', weight: 19 }, { name: 'Diyarbakır', weight: 18 }, { name: 'Şanlıurfa', weight: 15 },
  { name: 'Hatay', weight: 15 }, { name: 'Manisa', weight: 15 }, { name: 'Kayseri', weight: 14 },
  { name: 'Samsun', weight: 14 }, { name: 'Balıkesir', weight: 12 }, { name: 'Tekirdağ', weight: 11 },
  { name: 'Sakarya', weight: 11 }, { name: 'Aydın', weight: 11 }, { name: 'Denizli', weight: 10 },
  { name: 'Muğla', weight: 10 }, { name: 'Eskişehir', weight: 9 }, { name: 'Trabzon', weight: 8 },
  { name: 'Malatya', weight: 8 }, { name: 'Erzurum', weight: 7 }, { name: 'Van', weight: 9 },
  { name: 'Ordu', weight: 7 }, { name: 'Çanakkale', weight: 5 }, { name: 'Edirne', weight: 4 },
];

const TEAMS: string[] = [
  'Galatasaray', 'Fenerbahçe', 'Beşiktaş', 'Trabzonspor', 'GS', 'FB', 'BJK', 'TS',
  'cimbom', 'fener', 'kartal', 'sarı kırmızı', 'sarı lacivert', 'siyah beyaz', 'bordo mavi', 'Çarşı',
  'Göztepe', 'Bursaspor', 'Samsunspor', 'Kocaelispor', 'Eskişehirspor', 'Konyaspor', 'Antalyaspor', 'Sivasspor',
  'Altay', 'Karşıyaka', 'Amedspor', 'Adana Demirspor', 'Lakers', 'Golden State', 'Real Madrid', 'Barcelona',
];

const SCHOOLS: string[] = [
  'İTÜ', 'ODTÜ', 'Boğaziçi', 'Hacettepe', 'Ege', 'DEÜ', 'Marmara', 'YTÜ', 'Anadolu', 'AÖF',
  'İÜ', 'Gazi', 'Ankara Üni', 'Bilkent', 'Sabancı', 'Koç', 'Yeditepe', 'Bahçeşehir', 'Akdeniz', 'Uludağ',
  'Selçuk', 'KTÜ', 'Erciyes', 'Atatürk Üni', 'Çukurova', 'Pamukkale', 'Sakarya Üni', 'Kocaeli Üni', 'Mimar Sinan', 'GTÜ',
  'Galatasaray Üni', 'OMÜ', 'Ondokuz Mayıs', 'açıköğretim', 'hazırlık', 'lise son', 'Dicle', 'ESOGÜ', 'Kırıkkale', 'Trakya',
];

const JOBS: string[] = [
  'hemşire', 'yazılımcı', 'öğrenci', 'barista', 'makine müh. öğrencisi', 'öğretmen', 'mühendis', 'garson', 'kasiyer', 'satış danışmanı',
  'muhasebeci', 'grafik tasarımcı', 'avukat', 'stajyer avukat', 'doktor', 'tıp öğrencisi', 'diş hekimi', 'eczacı', 'fizyoterapist', 'diyetisyen',
  'polis', 'asker', 'kuaför', 'berber', 'aşçı', 'şef yardımcısı', 'kurye', 'şoför', 'çağrı merkezi', 'bankacı',
  'mimar', 'iç mimar', 'elektrikçi', 'tekniker', 'teknisyen', 'veteriner', 'veteriner hekim adayı', 'psikolog', 'psikoloji öğrencisi', 'sosyal medya uzmanı',
  'pazarlama', 'insan kaynakları', 'serbest çalışan', 'esnaf', 'kendi işim', 'personal trainer', 'yüzme hocası', 'müzisyen', 'dj', 'fotoğrafçı',
  'ebe', 'sağlık memuru', 'laborant', 'kabin memuru', 'otel resepsiyon', 'turizm', 'emlakçı', 'plasiyer', 'depo sorumlusu', 'fabrika işçisi',
  'işletme öğrencisi', 'hukuk öğrencisi', 'ilahiyat öğrencisi', 'öğretmen adayı', 'kpss çalışıyorum', 'yks hazırlık', 'yüksek lisans', 'askerden yeni geldim', 'iş arıyorum', 'ev hanımı',
];

const LYRICS_OR_QUOTES: string[] = [
  'gönül ne kahve ister ne kahvehane',
  'her şey olacağına varır',
  'akışına bırak',
  'hayat kısa, kuşlar uçuyor',
  'gel ne olursan ol yine gel',
  'bu da geçer ya hu',
  'damlaya damlaya göl olur',
  'az olsun öz olsun',
  'ya sabır',
  'hayırlısı',
  'kısmetse olur',
  'her şey güzel olacak',
  'dert etme',
  'keyfim yerinde',
  'bugün de yaşadık',
  'sakin ol şampiyon',
  'gülümse',
  'bir derdim var',
  'unuttun mu beni',
  'sen ağlama',
  'yalan dünya',
  'mutluluk bir yolculuktur',
  'ne güzel şey hatırlamak seni',
  'güzel günler göreceğiz çocuklar',
  'aşk acıtır',
  'olmaz deme, olmaz olmaz',
  'yaşamak bir ağaç gibi tek ve hür',
  'insan sevdiği yerde yaşar',
  'zor değil, sadece zaman',
  'ben yoluma giderim',
  'kalbim seninle',
  'hayat devam ediyor',
  'tutunamayanlardan',
  'gün olur devran döner',
];

const PETS: string[] = [
  '2 kedi annesi', "köpeğim Paşa'nın sahibi", '3 kedi 1 köpek', "kedim Pamuk'un hizmetçisi", 'golden sahibi',
  'sokak kedilerinin mama sponsoru', 'kedi babası', 'köpek annesi', "Duman'ın annesi", "Zeytin'in babası",
  'bir tekir ve bir sarman', 'evde bir muhabbet kuşu var', 'balıklarım bile beni sevmiyor', 'kedisiz yapamam', 'husky sahibi',
  "kedim Fıstık'la yaşıyorum", 'sahiplendiğim bir sokak köpeğim var', 'kedim benden ünlü', 'iki kedi bir kaplumbağa', "Boncuk'un insanı",
  'kedi alerjim var ama yine de seviyorum', 'beagle sahibi', 'van kedisi sahibi', "Karamel'in annesi", 'köpeğimle sabah yürüyüşleri',
];

const ZODIAC: Record<ZodiacSign, string> = {
  aries: 'Koç', taurus: 'Boğa', gemini: 'İkizler', cancer: 'Yengeç', leo: 'Aslan', virgo: 'Başak',
  libra: 'Terazi', scorpio: 'Akrep', sagittarius: 'Yay', capricorn: 'Oğlak', aquarius: 'Kova', pisces: 'Balık',
};

const TOPIC_TEMPLATES: Record<BioTopic, string[]> = {
  zodiac: ['{zodiac}', '{zodiac} burcu', '{zodiac} ama iyi olanından', 'burç {zodiac}, ona göre', '{zodiac} burcuyum kusura bakma', 'yükselen değil güneş {zodiac}'],
  team: ['{team}', '{team} taraftarı', 'kalbim {team}', '{team} maçı varsa yazma', 'tek aşkım {team}', '{team} sevmeyen kaydırsın'],
  school: ['{school}', '{school} mezunu', '{school} öğrencisi', 'okul: {school}', '{school} / {city}'],
  job: ['{job}', '{job} | {city}', 'meslek: {job}', '{job}, gerisi sohbette', 'şimdilik {job}'],
  hobby: ['{hobby}', '{hobby} bağımlısı', '{hobby} sever', 'hobi: {hobby}', '{hobby} olmadan olmaz', 'boş vaktimde {hobby}'],
  lyric: ['{lyric}', '"{lyric}"', '{lyric}..', '{lyric} diyorum ben'],
  oneLiner: ['{oneLiner}', '{oneLiner} | {city}', '{city} | {oneLiner}', '{oneLiner}, {trait}'],
  height: ['{height}', 'boy {height}', '{height} boyunda', 'boyum {height}, sormadan söyleyeyim'],
  pet: ['{pet}', '{pet}, ona göre', '{pet} olarak buradayım', '{pet} | {city}'],
};

/** Turkish users write "1.78" far more often than "178"; every fourth height is written as plain cm. */
function formatHeight(cm: number): string {
  if (cm % 4 === 0) return String(cm);
  return `${Math.floor(cm / 100)}.${String(cm % 100).padStart(2, '0')}`;
}

export const TR_BIO: BioBanks = {
  oneLiners: {
    funny: ONE_LINERS_FUNNY,
    catchy: ONE_LINERS_CATCHY,
    cringe: ONE_LINERS_CRINGE,
    sincere: ONE_LINERS_SINCERE,
  } satisfies Record<OneLinerRegister, string[]>,
  hobbies: HOBBIES,
  traits: TRAITS,
  cities: CITIES,
  teams: TEAMS,
  schools: SCHOOLS,
  jobs: JOBS,
  lyricsOrQuotes: LYRICS_OR_QUOTES,
  pets: PETS,
  zodiac: ZODIAC,
  topicTemplates: TOPIC_TEMPLATES,
  formatHeight,
  emojis: {
    general: [
      '🎵', '🎧', '🎬', '☕', '📚', '📷', '✈️', '🌊', '🏔️', '🐈',
      '🐶', '✨', '🔥', '🌙', '☀️', '🎸', '🍕', '🌿', '😎', '💫',
      '🫶', '🍃', '🎤', '🧿', '🌅', '🍀', '😴', '🤍', '🖤', '😂',
    ],
    female: ['🌸', '✨', '💅', '🦋', '🌙', '🎀', '💖', '🌷', '🍓', '💫', '🌺', '🤍', '🫧', '🐰', '💜'],
    male: ['⚽', '💪', '🏋️', '🚗', '🎮', '🏀', '🏍️', '🦅', '🦁', '🐺', '🎯', '🔥', '🥊', '😎', '🏁'],
  },
  imperfections: {
    substitutions: [
      [/\bbir şey\b/gi, 'bişey'],
      [/\bne yapıyorsun\b/gi, 'napıyosun'],
      [/\bne haber\b/gi, 'naber'],
      [/\byapıyorum\b/gi, 'yapıyom'],
      [/\bbilmiyorum\b/gi, 'bilmiyom'],
      [/\bseviyorum\b/gi, 'seviyom'],
      [/\bistiyorum\b/gi, 'istiyom'],
      [/\bgeliyorum\b/gi, 'geliyom'],
      [/\bdiyorum\b/gi, 'diyom'],
      [/\bherkes\b/gi, 'herkez'],
      [/\bben de\b/gi, 'bende'],
      [/\bsen de\b/gi, 'sende'],
      [/\btamam\b/gi, 'tmm'],
      [/\bmerhaba\b/gi, 'mrb'],
    ],
    lowercaseRate: 0.45,
    dropDiacriticsRate: 0.15,
    noFinalPunctRate: 0.7,
  },
  genderTopicBias: {
    female: { zodiac: 2.2, pet: 1.6, lyric: 1.3, hobby: 1.1, height: 0.4, team: 0.6 },
    male: { team: 2.2, height: 1.6, job: 1.3, school: 1.1, zodiac: 0.6, pet: 0.7 },
  },
};
